import { useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { UserViewContext } from './types';
import deriveUserViewContext from './helpers/deriveUserViewContext';

/**
 * Hook that derives semantic context about what the user is viewing from the current URL.
 * Returns a reactive UserViewContext object that updates when the route changes.
 *
 * Example usage:
 * ```tsx
 * const viewContext = useUserViewContext();
 * console.log(viewContext.description); // "User is in the workflow builder for workflow wf_123"
 * console.log(viewContext.capabilities); // ["Add, remove, and configure workflow nodes", ...]
 * ```
 */
const useUserViewContext = (): UserViewContext => {
    const params = useParams();
    const { pathname, search } = useLocation();

    return useMemo(() => {
        const searchParams = new URLSearchParams(search);
        const queryParams = Object.fromEntries(searchParams.entries());
        return deriveUserViewContext({ pathname, params, queryParams });
    }, [pathname, params, search]);
};

export default useUserViewContext;
